import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  HiOutlineUser,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeOff,
} from 'react-icons/hi'
import toast from 'react-hot-toast'
import api from '../services/api'

function Register() {
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    user_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [registrationSuccess, setRegistrationSuccess] = useState(null) // { employee_id, message }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    if (error) {
      setError('')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (!formData.user_name || !formData.password) {
      setError('Vui lòng nhập đầy đủ thông tin bắt buộc')
      return
    }

    // Username validation: only letters, numbers, underscore
    if (!/^[a-zA-Z0-9_]+$/.test(formData.user_name)) {
      setError('Tên đăng nhập chỉ được chứa chữ cái, số và dấu gạch dưới')
      return
    }

    if (formData.user_name.length < 3 || formData.user_name.length > 30) {
      setError('Tên đăng nhập phải từ 3-30 ký tự')
      return
    }

    if (formData.password.length < 6) {
      setError('Mật khẩu phải có ít nhất 6 ký tự')
      return
    }

    if (formData.password !== formData.confirm_password) {
      setError('Mật khẩu xác nhận không khớp')
      return
    }

    // Simple email validation (only if email is provided)
    if (formData.email && (!formData.email.includes('@') || !formData.email.includes('.'))) {
      setError('Email không hợp lệ')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const response = await api.post('/auth/register', {
        user_name: formData.user_name,
        email: formData.email || null,
        phone: formData.phone || null,
        password: formData.password,
      })

      if (response.data.status === 'success') {
        // Show success with user_name
        setRegistrationSuccess({
          user_name: formData.user_name,
          message: response.data.message
        })
        toast.success('Đăng ký thành công!')
      } else {
        setError(response.data.message || 'Đăng ký thất bại')
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Đăng ký thất bại. Vui lòng thử lại.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800 px-4 py-8">
      <div className="max-w-md w-full">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg mb-4 hover:shadow-xl transition-shadow">
              <img
                src="/logo_dat_xanh_mien_trung.png"
                alt="Đất Xanh Miền Trung"
                className="w-16 h-16 object-contain"
              />
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-white">CÔNG CỤ MARKETING</h1>
          <p className="text-primary-200 mt-2">Đất Xanh Miền Trung</p>
        </div>

        {/* Register Form */}
        <div className="card p-8">
          {registrationSuccess ? (
            // Success State
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Đăng ký thành công!
              </h2>
              <p className="text-gray-600 mb-4">
                {registrationSuccess.message}
              </p>

              {/* Show login info */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-amber-800 font-medium mb-2">
                  Thông tin đăng nhập của bạn:
                </p>
                <div className="bg-white rounded px-3 py-2 border border-amber-300">
                  <p className="text-xs text-gray-500">Tên đăng nhập</p>
                  <p className="text-lg font-mono font-bold text-primary-600">
                    {registrationSuccess.user_name}
                  </p>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  Bạn có thể đăng nhập bằng: Tên đăng nhập, Email hoặc SĐT (nếu đã nhập)
                </p>
              </div>

              <Link
                to="/login"
                className="btn-primary w-full py-3 inline-flex items-center justify-center"
              >
                Đi đến trang đăng nhập
              </Link>
            </div>
          ) : (
            // Register Form
            <>
          <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
            Đăng ký tài khoản
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Tên đăng nhập <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="nguyenvana"
                  autoComplete="username"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Chỉ chữ cái, số và dấu gạch dưới (_)</p>
            </div>

            {/* Email (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineMail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="email@example.com"
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Phone (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Số điện thoại
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlinePhone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="0912 345 678"
                  autoComplete="tel"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Mật khẩu <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineLockClosed className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="input pl-10 pr-10"
                  placeholder="Ít nhất 6 ký tự"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <HiOutlineEyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <HiOutlineEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Xác nhận mật khẩu <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineLockClosed className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className="input pl-10 pr-10"
                  placeholder="Nhập lại mật khẩu"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <HiOutlineEyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <HiOutlineEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Đang xử lý...
                </span>
              ) : (
                'Đăng ký'
              )}
            </button>
          </form>

          {/* Login link */}
          <p className="text-center text-sm text-gray-600 mt-6">
            Đã có tài khoản?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Đăng nhập ngay
            </Link>
          </p>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-primary-200 text-sm mt-6">
          © 2024 Đất Xanh Miền Trung. All rights reserved.
        </p>
      </div>
    </div>
  )
}

export default Register
